/**
 * PageSEO Component
 *
 * Reusable SEO component for managing page-level meta tags,
 * Open Graph tags, Twitter Cards, and canonical URLs.
 *
 * Usage:
 * ```typescript
 * <PageSEO
 *   title="ROI Calculator"
 *   description="Calculate your automation ROI..."
 *   keywords="ROI calculator, automation"
 *   ogImage="/og-image-tools.png"
 * />
 * ```
 */

import React from "react";
import { Helmet } from "react-helmet-async";

interface PageSEOProps {
  /** Page title (will be suffixed with " | AI Genius Automations") */
  title: string;

  /** Meta description (155-160 characters recommended) */
  description: string;

  /** Comma-separated keywords (optional) */
  keywords?: string;

  /** Path to Open Graph image (will be prefixed with domain) */
  ogImage?: string;

  /** Open Graph type (default: 'website') */
  ogType?: "website" | "article" | "product";

  /** Canonical URL (defaults to current page URL) */
  canonical?: string;

  /** Prevent search engine indexing (default: false) */
  noindex?: boolean;

  /** JSON-LD structured data */
  structuredData?: object | object[];
}

export const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  keywords,
  ogImage = "/og-image-default.png",
  ogType = "website",
  canonical,
  noindex = false,
  structuredData,
}) => {
  const fullTitle = `${title} | AI Genius Automations`;
  const fullOgImage = ogImage.startsWith("http")
    ? ogImage
    : `https://aigeniusautomations.com${ogImage}`;

  // Default canonical to current URL if not provided
  const canonicalUrl =
    canonical ||
    (typeof window !== "undefined"
      ? `https://aigeniusautomations.com${window.location.pathname}`
      : "https://aigeniusautomations.com");

  // Convert structured data to array if single object
  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="AI Genius Automations" />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="AI Genius Automations" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="AI Genius Automations - Break the Profitability Paradox"
      />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta
        name="twitter:image:alt"
        content="AI Genius Automations - Custom AI Automation Solutions"
      />

      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

/**
 * Default SEO component for pages without custom SEO
 * Uses generic site-wide meta tags
 */
export const DefaultPageSEO: React.FC<{ title: string }> = ({ title }) => (
  <PageSEO
    title={title}
    description="Break the profitability paradox. Scale revenue without sacrificing margins through AI-powered automation. Milestone-based pricing, enterprise-grade execution."
    keywords="AI automation, custom software, business automation, workflow optimization, ERP integration"
  />
);
